/**
 * The receipt under an answer: whether every figure it states came from a
 * governed query, and what those queries were.
 *
 * The verdict is always visible because it is the part that changes what a
 * reader does next. The receipt itself (measures, filters, period, joins, the
 * contract version and the generated SQL) is one click away, for whoever needs
 * to check or reproduce the figure.
 */
import { AlertTriangle, CalendarClock, ReceiptText, ShieldCheck } from "lucide-react";
import type { AnswerReceipt, QueryReceipt } from "../../../services/answerReceipt";
import { memberLabel } from "../../../services/chartOptions";
import {
  answerEndorsementLabel,
  answerPeriodNotes,
  describeFilter,
  describeJoin,
  describeMatch,
  describeOrder,
  describePeriod,
  describeRows,
  endorsementLabel,
  measureLabel,
} from "./answerReceiptView";

interface Props {
  receipt: AnswerReceipt;
}

function Row({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="receipt-row">
      <dt>{label}</dt>
      <dd>{values.length ? values.join("; ") : "None"}</dd>
    </div>
  );
}

function QueryDetail({ receipt, index, total }: {
  receipt: QueryReceipt;
  index: number;
  total: number;
}) {
  return (
    <section className="receipt-query" aria-label={`Query ${index + 1} of ${total}`}>
      {total > 1 && <h4>Query {index + 1}</h4>}
      <dl>
        <Row label="Measures" values={receipt.measures.map((m) => measureLabel(receipt, m))} />
        <Row label="Broken down by" values={receipt.dimensions.map((m) => memberLabel(m))} />
        <Row label="Filters" values={receipt.filters.map(describeFilter)} />
        <Row label="Period" values={receipt.timeDimensions.map(describePeriod)} />
        {receipt.segments.length > 0 && (
          <Row label="Segments" values={receipt.segments.map((m) => memberLabel(m))} />
        )}
        <Row label="Sorted by" values={receipt.order.map(describeOrder)} />
        <Row label="Joins" values={receipt.joins.map(describeJoin)} />
        <Row label="Rows" values={[describeRows(receipt)]} />
        <Row
          label="Contract"
          values={[
            `${receipt.contextId} v${receipt.contextVersion}, ${endorsementLabel(receipt)}`,
          ]}
        />
        {receipt.executedAt && (
          <Row label="Run at" values={[new Date(receipt.executedAt).toLocaleString()]} />
        )}
      </dl>
      {receipt.joins.length > 0 && (
        <ul className="receipt-join-conditions" aria-label="Join conditions">
          {receipt.joins.map((join) => (
            <li key={`${join.fromEntity}-${join.toEntity}`}><code>{join.condition}</code></li>
          ))}
        </ul>
      )}
      {receipt.sourceSql.length > 0 && (
        <details className="receipt-sql">
          <summary>Generated SQL</summary>
          {receipt.sourceSql.map((statement, position) => (
            <pre key={position}>{statement}</pre>
          ))}
        </details>
      )}
    </section>
  );
}

export default function AnswerReceiptPanel({ receipt }: Props) {
  const flagged = receipt.status === "unverified";
  const Icon = flagged ? AlertTriangle : ShieldCheck;
  const standing = answerEndorsementLabel(receipt.receipts);
  const periodNotes = answerPeriodNotes(receipt.receipts);
  return (
    <aside
      className={`answer-receipt ${flagged ? "flagged" : "verified"}`}
      aria-label="Answer check and receipt"
    >
      <p className="answer-receipt-verdict" role={flagged ? "alert" : undefined}>
        <Icon size={14} aria-hidden="true" />
        <span>{receipt.summary}</span>
      </p>
      {standing && (
        <p className="answer-receipt-standing">Semantic model: {standing}</p>
      )}
      {periodNotes.length > 0 && (
        <ul className="receipt-period-notes" aria-label="Incomplete periods">
          {periodNotes.map((note) => (
            <li key={note}>
              <CalendarClock size={13} aria-hidden="true" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
      {receipt.figures.length > 0 && (
        <ul className="receipt-figures" aria-label="Figures checked">
          {receipt.figures.map((figure, position) => (
            <li key={`${figure.text}-${position}`} className={figure.status}>
              <span className="figure-text">{figure.text}</span>
              <span className="figure-source">{describeMatch(figure)}</span>
            </li>
          ))}
        </ul>
      )}
      {receipt.receipts.length > 0 && (
        <details className="receipt-detail">
          <summary>
            <ReceiptText size={13} aria-hidden="true" />
            Receipt: what this answer was computed from
          </summary>
          {receipt.receipts.map((item, index) => (
            <QueryDetail
              key={item.queryId || index}
              receipt={item}
              index={index}
              total={receipt.receipts.length}
            />
          ))}
        </details>
      )}
    </aside>
  );
}
